/**
 * Client-side Auth Implementation
 * 
 * Uses localStorage as the storage backend with Web Crypto API for password hashing.
 * No server required — works on Cloudflare Pages SPA.
 */

// ============================================================================
// Storage Keys
// ============================================================================

const USERS_KEY = "text_tools_users";
const SESSION_KEY = "text_tools_session";
const SESSION_TIMEOUT_KEY = "text_tools_session_timeout";
const STATS_KEY = "text_tools_stats";

// ============================================================================
// Type Definitions
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: string;
  userId: string;
  expiresAt: string;
  user: User;
}

export interface AuthResult {
  success: boolean;
  message: string;
  user?: User;
  session?: Session;
  token?: string;
}

export interface StatsRecord {
  id: string;
  userId: string;
  textContent: string;
  statsJson: string;
  createdAt: string;
}

interface StoredUser extends User {
  passwordHash: string;
  salt: string;
}

// ============================================================================
// Password Hashing (Web Crypto API)
// ============================================================================

async function hashPassword(password: string, salt?: string): Promise<{ hash: string; salt: string }> {
  const useSalt = salt || crypto.randomUUID();
  const encoder = new TextEncoder();
  const data = encoder.encode(password + useSalt);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  return { hash: hashHex, salt: useSalt };
}

// ============================================================================
// Storage Helpers
// ============================================================================

function getUsers(): StoredUser[] {
  try {
    return JSON.parse(localStorage.getItem(USERS_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveUsers(users: StoredUser[]): void {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function getInternalSession(): Session | null {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: Session = JSON.parse(sessionStr);
    const timeoutStr = localStorage.getItem(SESSION_TIMEOUT_KEY);

    if (timeoutStr) {
      const timeout = parseInt(timeoutStr, 10);
      if (Date.now() > timeout) {
        clearSession();
        return null;
      }
    }

    return session;
  } catch {
    return null;
  }
}

function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  const timeout = Date.now() + 7 * 24 * 60 * 60 * 1000;
  localStorage.setItem(SESSION_TIMEOUT_KEY, timeout.toString());
}

function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_TIMEOUT_KEY);
}

// ============================================================================
// Public API
// ============================================================================

export async function registerUser(
  email: string,
  password: string,
  name: string
): Promise<AuthResult> {
  const users = getUsers();

  if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
    return { success: false, message: "Email already registered" };
  }

  const { hash, salt } = await hashPassword(password);
  const userId = crypto.randomUUID();
  const now = new Date().toISOString();

  const user: User = {
    id: userId,
    email: email.toLowerCase(),
    name,
    createdAt: now,
    updatedAt: now,
  };

  const storedUser: StoredUser = {
    ...user,
    passwordHash: hash,
    salt,
  };

  users.push(storedUser);
  saveUsers(users);

  const session: Session = {
    id: crypto.randomUUID(),
    userId: user.id,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    user,
  };

  saveSession(session);
  const token = btoa(JSON.stringify({ userId: user.id, sessionId: session.id }));

  return { success: true, message: "Registration successful", user, session, token };
}

export async function loginUser(
  email: string,
  password: string
): Promise<AuthResult> {
  const users = getUsers();
  const storedUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());

  if (!storedUser) {
    return { success: false, message: "Invalid email or password" };
  }

  const { hash } = await hashPassword(password, storedUser.salt);
  if (hash !== storedUser.passwordHash) {
    return { success: false, message: "Invalid email or password" };
  }

  const user: User = {
    id: storedUser.id,
    email: storedUser.email,
    name: storedUser.name,
    createdAt: storedUser.createdAt,
    updatedAt: storedUser.updatedAt,
  };

  const session: Session = {
    id: crypto.randomUUID(),
    userId: user.id,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    user,
  };

  saveSession(session);
  const token = btoa(JSON.stringify({ userId: user.id, sessionId: session.id }));

  return { success: true, message: "Login successful", user, session, token };
}

export function getCurrentSession(): Session | null {
  return getInternalSession();
}

export function logout(): void {
  clearSession();
}

export function getSession(): { userId: string; email: string; name: string } | null {
  const session = getInternalSession();
  if (!session) return null;
  return {
    userId: session.user.id,
    email: session.user.email,
    name: session.user.name,
  };
}

// ============================================================================
// Stats Records
// ============================================================================

export function saveStatsRecord(textContent: string, statsJson: string): void {
  const session = getInternalSession();
  if (!session) return;

  const records = getStatsRecords();
  records.unshift({
    id: crypto.randomUUID(),
    userId: session.userId,
    textContent,
    statsJson,
    createdAt: new Date().toISOString(),
  });

  localStorage.setItem(STATS_KEY, JSON.stringify(records.slice(0, 50)));
}

export function getStatsRecords(): StatsRecord[] {
  try {
    const session = getInternalSession();
    if (!session) return [];

    const all = JSON.parse(localStorage.getItem(STATS_KEY) || "[]");
    return all.filter((r: StatsRecord) => r.userId === session.userId);
  } catch {
    return [];
  }
}
